import type { RootState } from '@/store';
import { PROJECT_FILES_TAG } from '@/api/constants';
import { api } from '@/api/core';
import { getProjectFilesApi } from './getProjectFiles';
import { updateMockProjectFile } from './mockProjectFiles';
import { mockDelay, readMockProjectError } from './mockUtils';

export const PROJECT_FILE_DESCRIPTION_MAX_LENGTH = 200;

/** Shape assumed until the backend contract exists */
export interface UpdateProjectFileRequest {
  projectId: string;
  fileId: string;
  /** Null clears the description. */
  description: string | null;
}

const MOCK_RESPONSE_DELAY_MS = 400;

const updateProjectFileApi = api.injectEndpoints({
  endpoints: (builder) => ({
    updateProjectFile: builder.mutation<void, UpdateProjectFileRequest>({
      // The backend `PATCH /projects/{projectId}/files/{fileId}` endpoint does not exist yet: edit the mock list (see mockProjectFiles.ts).
      // The change lands before the delay, so a poll in flight cannot bring back the old text.
      queryFn: async ({ projectId, fileId, description }) => {
        const mockError = readMockProjectError();
        const updated = !mockError && updateMockProjectFile(projectId, fileId, { description });
        await mockDelay(MOCK_RESPONSE_DELAY_MS);
        if (mockError) return { error: mockError };
        return updated ? { data: undefined } : { error: { status: 404, data: 'File not found' } };
      },
      // Shows the new description straight away in every cached page, and rolls it back if the save fails.
      async onQueryStarted({ projectId, fileId, description }, { dispatch, getState, queryFulfilled }) {
        const patches = getProjectFilesApi.util
          .selectCachedArgsForQuery(getState() as RootState, 'getProjectFiles')
          .filter((args) => args.projectId === projectId)
          .map((args) =>
            dispatch(
              getProjectFilesApi.util.updateQueryData('getProjectFiles', args, (draft) => {
                const file = draft.files.find((projectFile) => projectFile.fileId === fileId);
                if (file) file.description = description;
              }),
            ),
          );

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: (_result, error, { projectId }) => (error ? [] : [{ type: PROJECT_FILES_TAG, id: projectId }]),
    }),
  }),
});

export const { useUpdateProjectFileMutation } = updateProjectFileApi;
